import React, { FC } from "react";
import { v4 as uuidv4 } from "uuid";

import { useCheckout } from "@modules/Purchasing/hooks/useCheckout";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { useDataLayer } from "@Libs/DataLayer/hooks/useDataLayer";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { IUnsavedBasket } from "@modules/Purchasing/services/baskets";
import { IErrorResponse } from "@api/createApi";
import { ISubscriptionPlan } from "@modules/Subscriptions/types";
import {
  isSubscriptionProduct,
  getSubscriptionPeriod,
} from "@modules/Subscriptions/services/subscriptionPlans";
import { ChooseSubscriptionPlanActivationRate } from "@modules/Subscriptions/Flows/Subscribe/ChooseSubscriptionPlan/ABTests/ActivationRate/ChooseSubscriptionPlanActivationRate";
import { Pay } from "@modules/PaymentProcessing/Flows/Pay/Pay";

export const ChoosePlanForCheckout: FC = () => {
  const { navigateToStep, onLeave, state: { relatedServiceId } } = useCheckout();
  const { currentBasket, saveBasket, isSavingBasket } = useCurrentBasket();
  const dataLayer = useDataLayer();
  const navigate = useNavigate();

  const existingSubscriptionItem = currentBasket?.items?.find(({ item }) =>
    isSubscriptionProduct(item)
  )?.item;

  const initialPeriod =
    existingSubscriptionItem &&
    isSubscriptionProduct(existingSubscriptionItem) &&
    getSubscriptionPeriod(existingSubscriptionItem.name) === "year"
      ? ("year" as const)
      : ("quarter" as const);

  const handleLockedBasket = ({ items }: { items: IUnsavedBasket["items"] }) => {
    dataLayer.setQueryData("currentBasketId", null);
    const id = uuidv4();
    saveBasket(
      { id, items },
      {
        onSuccess: (basket) => {
          dataLayer.setQueryData("currentBasketId", id);
          dataLayer.setQueryData("currentBasket", basket);
        },
      }
    );
  };

  const handleSelect = (paymentPlan: ISubscriptionPlan) => {
    const nonSubscriptionItems = currentBasket.items.filter(
      (basketItem) => basketItem.itemType !== "product"
    );
    const items: IUnsavedBasket["items"] = [
      ...nonSubscriptionItems.map(({ itemId, itemType, quantity }) => ({
        itemId,
        itemType,
        quantity,
      })),
      { itemId: paymentPlan.id, itemType: "product", quantity: 1 },
    ];
    saveBasket(
      { id: currentBasket.id, items },
      {
        onSuccess: () => navigateToStep(Pay.root),
        onError: async (error: IErrorResponse) => {
          if (error.response?.status === 423) {
            handleLockedBasket({ items });
            navigateToStep(Pay.root);
          } else {
            navigate(["SERVER_ERROR"]);
          }
        },
      }
    );
  };

  return (
    <ChooseSubscriptionPlanActivationRate
      onBack={onLeave}
      onSelect={handleSelect}
      isDisabled={isSavingBasket}
      initialPeriod={initialPeriod}
      serviceOfferId={relatedServiceId}
    />
  );
};
